import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogHeader, DialogTitle, DialogContent } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { ArrowLeft, FileText, AlertCircle, CheckCircle, Loader2, Info, Save, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { Signature } from '../production/tabs/Signature';
import { useTeamMembers } from '../../../../../redux/ProductionData/hooks/useTeamMembers';
import { generateMaterialReport, clearGenerateMaterialReport, getMaterialReportVersions } from '../../../../../redux/Project/materialsData/action';
import { useReportDraft } from '../../../../../redux/ReportDrafts/hooks/useReportDraft';
import { REPORT_TYPES } from '../../../../../redux/ReportDrafts/types';
import { PdfViewer } from '../../../../../components/viewers/PdfViewer';
import { GenerateReportButton } from '../../../../../components/ui/GenerateReportButton';
import { ApproveButton } from '../../../../../components/ui/ApproveButton';
import { getMaterialSheetReportType } from './getMaterialSheetReportType';

interface PipeSizeData {
  pipeId: number | string;
  pipeName: string;
  size: number | string;
  wallThickness: number | string;
  projectTypeId?: number | string;
  projectType?: string;
  projectSubType?: string;
  displayName?: string;
  coating?: boolean;
}

interface MaterialReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number | string;
  projectName: string;
  pipeSizesData?: PipeSizeData[];
  selectedTabName?: string;
  reportType?: string;
  isCustomerRepresentativeUser?: boolean;
  isQCInspectorUser?: boolean;
  isWelderUser?: boolean;
  currentUserTeamMemberId?: string | number | null;
  canSign?: boolean;
  canEdit?: boolean;
  isAdminOrSuperAdmin?: boolean;
}

export const MaterialReportModal: React.FC<MaterialReportModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  pipeSizesData: pipeSizesDataProp = [],
  selectedTabName = 'Material',
  reportType = 'pipe',
  isCustomerRepresentativeUser = false,
  isQCInspectorUser = false,
  isWelderUser = false,
  currentUserTeamMemberId,
  canSign = true,
  canEdit = true,
  isAdminOrSuperAdmin = true,
}) => {
  const dispatch = useDispatch();
  const { teamMembers, loadTeamMembers } = useTeamMembers();

  // Get report generation state from Redux
  const {
    generateMaterialReportLoading,
    generateMaterialReportSuccess,
    generateMaterialReportError,
    generateMaterialReportData,
    materialReportVersionsLoading,
    materialReportVersions,
    materialReportVersionsError
  } = useSelector((state: any) => state.materialsData || {});

  // Versions expand/collapse state
  const [isVersionsExpanded, setIsVersionsExpanded] = useState<boolean>(false);

  // Pipe selection state
  const [selectedPipeIds, setSelectedPipeIds] = useState<string[]>([]);

  // Material category selection state - default to all available categories
  const [selectedMaterialCategories, setSelectedMaterialCategories] = useState<string[]>(['pipe', 'forge', 'gas', 'wire']);

  // PDF Viewer state
  const [viewerFile, setViewerFile] = useState<{ fileUrl: string; fileName: string } | null>(null);

  // Track if form has been modified (dirty state)
  const [isDirty, setIsDirty] = useState<boolean>(false);

  // Track if modal is reloading after save
  const [isReloading, setIsReloading] = useState<boolean>(false);

  // Track if draft has been saved at least once (required before generation)
  const [hasSavedDraft, setHasSavedDraft] = useState<boolean>(false);

  // Track if generation was initiated in current session (to prevent stale Redux state from opening viewer)
  const isGeneratingRef = useRef<boolean>(false);
  // Track if we're currently applying a draft to prevent setting isDirty during draft application
  const isApplyingDraftRef = useRef<boolean>(false);

  // Email state
  const [email, setEmail] = useState<string>('');
  const [emailList, setEmailList] = useState<string[]>([]);
  const [emailError, setEmailError] = useState<string>('');

  // Signature state
  const [qcList, setQcList] = useState<Array<{ value: string; label: string; name?: string; userId?: string }>>([]);
  const [customerRepresentativeList, setCustomerRepresentativeList] = useState<Array<{ value: string; label: string; name?: string; userId?: string }>>([]);
  const [selectedCustomerRepresentativeId, setSelectedCustomerRepresentativeId] = useState<string | null>(null);
  const [selectedQcInspectorId, setSelectedQcInspectorId] = useState<string | null>(null);
  const [signatureData, setSignatureData] = useState<any>(null);

  // Draft management
  const {
    draft,
    draftSignatures,
    updatedAt: draftUpdatedAt,
    isReadyToGenerate,
    missingFields,
    isFetchingDraft,
    isSavingDraft,
    saveDraftSuccess,
    saveDraftError,
    saveDraft,
    loadDraft,
    clearDraft,
    clearSaveStatus,
    draftId
  } = useReportDraft();

  const getreportTypeValueValue = (reportType: string) => getMaterialSheetReportType(reportType);

  // Clear global draft then fetch for this report type (layout phase avoids applying stale draft from another tab/type)
  useLayoutEffect(() => {
    if (isOpen && projectId) {
      clearDraft();
      loadDraft({
        projectId,
        reportType: getreportTypeValueValue(reportType),
      });
    }
  }, [isOpen, projectId, reportType, loadDraft, clearDraft]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Clear viewer state first
      setViewerFile(null);
      dispatch(clearGenerateMaterialReport());
    } else {
      // Reset all state when modal closes
      setViewerFile(null);
      setEmail('');
      setEmailList([]);
      setEmailError('');
      setSignatureData(null);
      setSelectedCustomerRepresentativeId(null);
      setSelectedQcInspectorId(null);
      setSelectedPipeIds([]);
      setSelectedMaterialCategories([]);
      // Reset draft tracking state
      setHasSavedDraft(false);
      setIsDirty(false);
      // Reset generating ref to prevent stale state
      isGeneratingRef.current = false;
      // Clear draft state
      clearDraft();
      // Clear Redux report state
      dispatch(clearGenerateMaterialReport());
    }
  }, [isOpen, projectId, dispatch, clearDraft]);

  // Handle applying draft to form
  const handleApplyDraft = useCallback(() => {
    if (!draft) return;


    console.log('draft', draft);
    console.log('draftId', draftId);

    // Mark that we're applying draft to prevent setting isDirty
    isApplyingDraftRef.current = true;

    // Populate selected pipe IDs - check both selectedPipeIds and pipeIds
    const pipeIds = draft.selectedPipeIds || draft.pipeIds || [];
    if (pipeIds.length > 0) {
      setSelectedPipeIds(pipeIds.map((id: any) => String(id)));
    }

    // Populate selected material categories
    const materialCategories = draft.formData?.selectedMaterialCategories || [];
    if (materialCategories.length > 0) {
      setSelectedMaterialCategories(materialCategories);
    }

    // Populate email list
    const emails = draft.emailList || draft.formData?.emailList || [];
    if (emails.length > 0) {
      setEmailList(emails);
    }

    // Populate signatures from draftSignatures (stored separately) - ensure stamp images are included
    // Merge with existing signature data to preserve current signatures if they exist
    if (draftSignatures) {
      const signatureDataFromDraft: any = {
        signature: {
          ...signatureData?.signature // Preserve existing signatures
        }
      };

      // Only apply draft QC signature if current signature doesn't exist or is empty
      if (draftSignatures.qc && draftSignatures.qc.signed) {
        const currentQcHasSignature = signatureData?.signature?.qc?.signature && signatureData.signature.qc.signature.trim();

        if (!currentQcHasSignature) {
          signatureDataFromDraft.signature.qc = {
            name: draftSignatures.qc.name || '',
            signature: draftSignatures.qc.signature || '',
            stampImage: draftSignatures.qc.stampImage || '', // Ensure stamp image is included
            timestamp: draftSignatures.qc.timestamp || '',
            comment: draftSignatures.qc.comment || ''
          };
          // Find matching team member by teamMemberId (preferred) or fallback to name
          const teamMemberId = draftSignatures.qc.teamMemberId?.toString();
          const matchingQc = teamMemberId
            ? qcList.find(m => m.value === teamMemberId)
            : qcList.find(m => m.name === draftSignatures.qc.name);
          if (matchingQc) {
            setSelectedQcInspectorId(matchingQc.value);
          } else if (qcList.length > 0 && !selectedQcInspectorId) {
            setSelectedQcInspectorId('draft-restored');
          }
        }
      }

      // Only apply draft customer signature if current signature doesn't exist or is empty
      if (draftSignatures.customer && draftSignatures.customer.signed) {
        const currentCustomerHasSignature = signatureData?.signature?.customer?.signature && signatureData.signature.customer.signature.trim();

        if (!currentCustomerHasSignature) {
          signatureDataFromDraft.signature.customer = {
            name: draftSignatures.customer.name || '',
            signature: draftSignatures.customer.signature || '',
            stampImage: draftSignatures.customer.stampImage || '', // Ensure stamp image is included
            timestamp: draftSignatures.customer.timestamp || '',
            comment: draftSignatures.customer.comment || ''
          };
          // Find matching team member by teamMemberId (preferred) or fallback to name
          const teamMemberId = draftSignatures.customer.teamMemberId?.toString();
          const matchingCustomer = teamMemberId
            ? customerRepresentativeList.find(m => m.value === teamMemberId)
            : customerRepresentativeList.find(m => m.name === draftSignatures.customer.name);
          if (matchingCustomer) {
            setSelectedCustomerRepresentativeId(matchingCustomer.value);
          } else if (customerRepresentativeList.length > 0 && !selectedCustomerRepresentativeId) {
            setSelectedCustomerRepresentativeId('draft-restored');
          }
        }
      }

      // Only update signature data if we have something to add or if current data is empty
      if (Object.keys(signatureDataFromDraft.signature).length > 0 || !signatureData) {
        setSignatureData(signatureDataFromDraft);
      }
    }

    // Reset dirty state after applying draft - use setTimeout to ensure all state updates complete first
    setTimeout(() => {
      setIsDirty(false);
      isApplyingDraftRef.current = false;
    }, 100);
    // Hide loader after draft is applied
    setIsReloading(false);
  }, [draft, draftSignatures, qcList, customerRepresentativeList]);

  // Auto-apply draft when it's loaded
  useEffect(() => {
    if (draft && !isFetchingDraft) {
      // Mark that we have a saved draft (existing draft loaded from server)
      setHasSavedDraft(true);
      // Clear reloading state when draft is successfully loaded
      setIsReloading(false);
      handleApplyDraft();
    }
  }, [draft, isFetchingDraft, handleApplyDraft]);

  // Auto-open PDF viewer when Material report generates successfully
  useEffect(() => {
    // Only open viewer if generation was initiated in this session (not from stale Redux state)
    if (generateMaterialReportSuccess && generateMaterialReportData && isGeneratingRef.current) {
      isGeneratingRef.current = false; // Reset after handling
      const fileUrl = generateMaterialReportData?.downloadUrl || generateMaterialReportData?.data?.downloadUrl;
      const fileName = generateMaterialReportData?.fileName || generateMaterialReportData?.data?.fileName || 'Material Report';
      if (fileUrl) {
        setViewerFile({ fileUrl, fileName });
      }
    }
  }, [generateMaterialReportSuccess, generateMaterialReportData]);

  // Reload draft after successful save to get fresh data from server
  useEffect(() => {
    if (saveDraftSuccess && projectId) {
      // Mark draft as saved and reset dirty state
      setHasSavedDraft(true);
      setIsDirty(false);
      // Clear save status to prevent re-triggering
      clearSaveStatus();
      // Show subtle reload indicator (doesn't block interaction)
      setIsReloading(true);
      // Reload draft from server to get fresh data including stamp images
      // This ensures we have the latest data with all IDs and stamp images
      loadDraft({
        projectId,
        reportType: getreportTypeValueValue(reportType)
      });
    }
  }, [saveDraftSuccess, projectId, loadDraft, reportType, clearSaveStatus]);

  const loadAllTeamMembers = useCallback(() => {
    if (!teamMembers) return;

    const qcInspectors = teamMembers
      .filter(
        (member: any) =>
          member?.team_member_role === 'Quality Control' ||
          member?.team_member_role === 'QC' ||
          member?.team_member_role === 'QA/QC'
      )
      .map((member: any) => ({
        value: member.team_member_id.toString(),
        name: `${member.user.first_name} ${member.user.last_name}`,
        label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`,
        userId: member?.user_id,
      }));

    const customerReps = teamMembers
      .filter(
        (member: any) =>
          member?.team_member_role === 'Customer Representative' ||
          member?.team_member_role === 'Client Representative'
      )
      .map((member: any) => ({
        value: member.team_member_id.toString(),
        name: `${member.user.first_name} ${member.user.last_name}`,
        label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`,
        userId: member?.user_id,
      }));

    console.log('teamMembers', teamMembers);

    console.log('qcInspectors', qcInspectors);
    console.log('customerReps', customerReps);

    if (isAdminOrSuperAdmin) {
      setQcList(qcInspectors);
    } else if (isQCInspectorUser && qcInspectors.length > 0) {
      setQcList(qcInspectors.filter((member: any) => member.userId === currentUserTeamMemberId));
    } else {
      setQcList([]);
    }

    if (isAdminOrSuperAdmin) {
      setCustomerRepresentativeList(customerReps);
    } else if (isCustomerRepresentativeUser && customerReps.length > 0) {
      setCustomerRepresentativeList(
        customerReps.filter((member: any) => member.userId === currentUserTeamMemberId)
      );
    } else {
      setCustomerRepresentativeList([]);
    }
  }, [
    teamMembers,
    isAdminOrSuperAdmin,
    isQCInspectorUser,
    isCustomerRepresentativeUser,
    currentUserTeamMemberId,
  ]);

  useEffect(() => {
    if (teamMembers && teamMembers.length > 0) {
      loadAllTeamMembers();
    }
  }, [teamMembers, loadAllTeamMembers]);

  useEffect(() => {
    if (isOpen && projectId) {
      {
        loadTeamMembers(projectId);
      }
    }
  }, [isOpen, projectId]);

  // Restore QC / customer selects from draft after role-filtered lists load (draft often arrives before team fetch completes)
  useEffect(() => {
    if (!isOpen || !draftSignatures) return;

    const qcSigned = draftSignatures.qc?.signed && draftSignatures.qc.name;
    const qcNeedsSlot =
      qcSigned &&
      qcList.length > 0 &&
      (!selectedQcInspectorId || selectedQcInspectorId === 'draft-restored');

    if (qcNeedsSlot) {
      const teamMemberId = draftSignatures.qc.teamMemberId?.toString();
      const matchingQc =
        (teamMemberId ? qcList.find(m => m.value === teamMemberId) : undefined) ||
        qcList.find(
          m =>
            m.name === draftSignatures.qc.name ||
            m.label === draftSignatures.qc.name ||
            (draftSignatures.qc.name && m.label?.includes(draftSignatures.qc.name)) ||
            (draftSignatures.qc.name && m.name?.includes(draftSignatures.qc.name))
        );
      if (matchingQc) {
        setSelectedQcInspectorId(matchingQc.value);
      }
    }

    const customerSigned = draftSignatures.customer?.signed && draftSignatures.customer.name;
    const customerNeedsSlot =
      customerSigned &&
      customerRepresentativeList.length > 0 &&
      (!selectedCustomerRepresentativeId || selectedCustomerRepresentativeId === 'draft-restored');

    if (customerNeedsSlot) {
      const teamMemberId = draftSignatures.customer.teamMemberId?.toString();
      const matchingCustomer =
        (teamMemberId ? customerRepresentativeList.find(m => m.value === teamMemberId) : undefined) ||
        customerRepresentativeList.find(
          m =>
            m.name === draftSignatures.customer.name ||
            m.label === draftSignatures.customer.name ||
            (draftSignatures.customer.name && m.label?.includes(draftSignatures.customer.name)) ||
            (draftSignatures.customer.name && m.name?.includes(draftSignatures.customer.name))
        );
      if (matchingCustomer) {
        setSelectedCustomerRepresentativeId(matchingCustomer.value);
      }
    }
  }, [
    isOpen,
    draftSignatures,
    qcList,
    customerRepresentativeList,
    selectedQcInspectorId,
    selectedCustomerRepresentativeId,
  ]);

  // Pipe selection handlers
  const handlePipeToggle = (pipeId: string) => {
    setSelectedPipeIds(prev => {
      if (prev.includes(pipeId)) {
        return prev.filter(id => id !== pipeId);
      } else {
        return [...prev, pipeId];
      }
    });
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  const handleSelectAllPipes = () => {
    if (selectedPipeIds.length === pipeSizesDataProp.length) {
      setSelectedPipeIds([]);
    } else {
      setSelectedPipeIds(pipeSizesDataProp.map(pipe => pipe.pipeId.toString()));
    }
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  // Material category handlers
  const handleMaterialCategoryToggle = (category: string) => {
    setSelectedMaterialCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(cat => cat !== category);
      } else {
        return [...prev, category];
      }
    });
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  const handleSelectAllCategories = () => {
    const availableCategories = getAvailableMaterialCategories();
    if (selectedMaterialCategories.length === availableCategories.length) {
      setSelectedMaterialCategories([]);
    } else {
      setSelectedMaterialCategories([...availableCategories]);
    }
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  // Get available material categories based on selected pipes
  const getAvailableMaterialCategories = useCallback(() => {
    const categories: string[] = ['pipe', 'forge', 'gas', 'wire'];

    // Check if any selected pipe has coating enabled
    const hasCoating = selectedPipeIds.some(pipeId => {
      const pipe = pipeSizesDataProp.find(p => p.pipeId.toString() === pipeId);
      return pipe?.coating === true;
    });

    if (hasCoating) {
      categories.push('coating');
    }

    return categories;
  }, [selectedPipeIds, pipeSizesDataProp]);

  // Auto-select all available categories when pipes are selected or coating becomes available
  useEffect(() => {
    const availableCategories = getAvailableMaterialCategories();
    // Only update if we have selected pipes and categories are available
    if (selectedPipeIds.length > 0 && availableCategories.length > 0) {
      // Check if we need to add missing categories (auto-select all by default)
      const missingCategories = availableCategories.filter(cat => !selectedMaterialCategories.includes(cat));
      // Check if we have invalid categories (categories not in available list)
      const invalidCategories = selectedMaterialCategories.filter(cat => !availableCategories.includes(cat));

      if (missingCategories.length > 0) {
        // Auto-select all available categories by default
        setSelectedMaterialCategories([...availableCategories]);
        if (!isApplyingDraftRef.current) {
          setIsDirty(true);
        }
      } else if (invalidCategories.length > 0) {
        // Remove invalid categories (e.g., coating removed when pipe without coating is selected)
        setSelectedMaterialCategories(prev => prev.filter(cat => availableCategories.includes(cat)));
        if (!isApplyingDraftRef.current) {
          setIsDirty(true);
        }
      }
    } else if (selectedPipeIds.length === 0) {
      // Reset to default when no pipes selected
      setSelectedMaterialCategories(['pipe', 'forge', 'gas', 'wire']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPipeIds, getAvailableMaterialCategories]);

  // Signature handlers
  const handleQcSignatureChange = (value: string | null) => {
    // If selection is changing, clear all signatures and set to null
    if (selectedQcInspectorId !== value) {
      setSignatureData({
        signature: {
          ...signatureData?.signature,
          qc: {
            name: '',
            signature: '',
            stampImage: '',
            timestamp: '',
            comment: ''
          }
        }
      });
    }
    setSelectedQcInspectorId(value);
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  const handleCustomerSignatureChange = (value: string | null) => {
    // If selection is changing, clear all signatures and set to null
    if (selectedCustomerRepresentativeId !== value) {
      setSignatureData({
        signature: {
          ...signatureData?.signature,
          customer: {
            name: '',
            signature: '',
            stampImage: '',
            timestamp: '',
            comment: ''
          }
        }
      });
    }
    setSelectedCustomerRepresentativeId(value);
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  const onHandleSignature = (data: { signature: string; name: string; comment?: string; stampImage?: string; type: 'customer' | 'qc' }) => {
    setSignatureData((prev: any) => ({
      ...prev,
      signature: {
        ...prev?.signature,
        [data.type]: {
          signature: data.signature,
          name: data.name,
          comment: data.comment,
          stampImage: data.stampImage,
          timestamp: new Date().toISOString()
        }
      }
    }));
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  // Process versions - sort by versionNumber descending, mark latest
  const processedVersions = useMemo(() => {
    if (!materialReportVersions || !Array.isArray(materialReportVersions) || materialReportVersions.length === 0) {
      return { latest: null, older: [] };
    }

    // Sort by versionNumber descending (latest first)
    const sortedVersions = [...materialReportVersions].sort((a: any, b: any) => {
      const versionA = a.details?.versionNumber || a.versionNumber || 0;
      const versionB = b.details?.versionNumber || b.versionNumber || 0;
      return versionB - versionA;
    });

    const latest = sortedVersions[0] || null;
    const older = sortedVersions.slice(1);

    return { latest, older };
  }, [materialReportVersions]);

  // Handle download/view
  const handleDownloadReport = (doc: any) => {
    if (!doc || !(doc.fileUrl || doc.file_url)) {
      return;
    }
    const url = doc.fileUrl || doc.file_url;
    const fileName = doc.fileName || doc.file_name || 'Material Report';
    setViewerFile({ fileUrl: url, fileName });
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Compute header info items for display (project)
  const headerInfoItems = useMemo(() => {
    const infoItems = [];

    // Add project name
    if (projectName) {
      infoItems.push({ label: 'Project', value: projectName, bgColor: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]' });
    }

    return infoItems;
  }, [projectName]);

  // Email handlers
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailError('');
  };

  const handleAddEmail = () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (emailList.includes(trimmedEmail)) {
      setEmailError('This email is already added');
      return;
    }

    setEmailList([...emailList, trimmedEmail]);
    setEmail('');
    setEmailError('');
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmailList(emailList.filter(e => e !== emailToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !generateMaterialReportLoading) {
      if (email.trim()) {
        handleAddEmail();
      }
    }
  };

  const handleGenerateReport = () => {
    if (!projectId) {
      setEmailError('Project ID is required');
      return;
    }

    if (selectedPipeIds.length === 0) {
      setEmailError('Please select at least one pipe size');
      return;
    }

    if (selectedMaterialCategories.length === 0) {
      setEmailError('Please select at least one material category');
      return;
    }

    // Validate signatures are provided
    if (!signatureData || (!signatureData.signature?.qc && !signatureData.signature?.customer)) {
      setEmailError('Please add at least one signature (QC or Customer Representative) before generating the report.');
      return;
    }

    // Prepare signatures object
    const signatures: any = {};

    if (signatureData.signature?.qc) {
      signatures.qc = {
        name: signatureData.signature.qc.name || '',
        timestamp: signatureData.signature.qc.timestamp || new Date().toISOString(),
        signature: signatureData.signature.qc.signature || '',
        stampImage: signatureData.signature.qc.stampImage || '', // Ensure stamp image is included in generate report
        comment: signatureData.signature.qc.comment || ''
      };
    }

    if (signatureData.signature?.customer) {
      signatures.customer = {
        name: signatureData.signature.customer.name || '',
        signature: signatureData.signature.customer.signature || '',
        timestamp: signatureData.signature.customer.timestamp || new Date().toISOString(),
        stampImage: signatureData.signature.customer.stampImage || '', // Ensure stamp image is included in generate report
        comment: signatureData.signature.customer.comment || ''
      };
    }

    // Build payload with pipeIds array and material categories
    const payload: any = {
      projectId,

      pipeIds: selectedPipeIds.map(id => parseInt(id, 10)),
      //  materialCategories: selectedMaterialCategories, // Array of strings: ['pipe', 'forge', 'gas', 'wire', 'coating']
      reportType: getreportTypeValueValue(reportType), // Add reportType based on selected tab
      mergeDocuments: true,
      signatures
    };

    // Pass emailAddress as array if emails are provided
    if (emailList.length > 0) {
      payload.emailAddress = emailList;
    }

    // Mark that generation was initiated in this session
    isGeneratingRef.current = true;
    dispatch(generateMaterialReport(payload));
  };

  // Handle save draft
  const handleSaveDraft = useCallback(() => {
    // Build signatures object - always include all keys to properly handle removals
    // Check for signatures in both signatureData state and draftSignatures (in case state hasn't updated yet)
    const currentQcSignature = signatureData?.signature?.qc;
    const currentCustomerSignature = signatureData?.signature?.customer;

    // Also check draftSignatures as fallback to preserve existing signatures
    const draftQcSignature = draftSignatures?.qc;
    const draftCustomerSignature = draftSignatures?.customer;

    // Use current signature if it exists and has a signature value, otherwise use draft signature
    const qcSignature = (currentQcSignature?.signature && currentQcSignature.signature.trim())
      ? currentQcSignature
      : (draftQcSignature?.signed && draftQcSignature?.signature) ? {
        name: draftQcSignature.name || '',
        signature: draftQcSignature.signature || '',
        stampImage: draftQcSignature.stampImage || '',
        timestamp: draftQcSignature.timestamp || '',
        comment: draftQcSignature.comment || ''
      } : null;

    const customerSignature = (currentCustomerSignature?.signature && currentCustomerSignature.signature.trim())
      ? currentCustomerSignature
      : (draftCustomerSignature?.signed && draftCustomerSignature?.signature) ? {
        name: draftCustomerSignature.name || '',
        signature: draftCustomerSignature.signature || '',
        stampImage: draftCustomerSignature.stampImage || '',
        timestamp: draftCustomerSignature.timestamp || '',
        comment: draftCustomerSignature.comment || ''
      } : null;

    const signatures: Record<string, any> = {
      qc: qcSignature ? {
        name: qcSignature.name || '',
        signature: qcSignature.signature || '',
        stampImage: qcSignature.stampImage || '',
        timestamp: qcSignature.timestamp || new Date().toISOString(),
        signed: true,
        comment: qcSignature.comment || '',
        teamMemberId: selectedQcInspectorId || draftQcSignature?.teamMemberId || null
      } : { signed: false },
      customer: customerSignature ? {
        name: customerSignature.name || '',
        signature: customerSignature.signature || '',
        stampImage: customerSignature.stampImage || '',
        timestamp: customerSignature.timestamp || new Date().toISOString(),
        signed: true,
        comment: customerSignature.comment || '',
        teamMemberId: selectedCustomerRepresentativeId || draftCustomerSignature?.teamMemberId || null
      } : { signed: false }
    };

    // Build form data object
    const formData = {
      selectedPipeIds,
      selectedMaterialCategories,
      emailList
    };


    //Build save draft payload
    const saveDraftPayload: any = {
      projectId,
      reportType: getreportTypeValueValue(reportType),
      pipeIds: selectedPipeIds.map(id => parseInt(id, 10)),
      formData,
      signatures
    }
    // Include draftId if it exists (for updating existing draft)
    if (draftId) {
      saveDraftPayload.draftId = draftId;
    }

    console.log('saveDraftPayload', saveDraftPayload);
    // Call save draft
    saveDraft(saveDraftPayload);


  }, [projectId, selectedPipeIds, selectedMaterialCategories, emailList, signatureData, draftSignatures, saveDraft, reportType, selectedQcInspectorId, selectedCustomerRepresentativeId]);

  const isLoading = generateMaterialReportLoading;
  const isSuccess = generateMaterialReportSuccess;

  // Check if form is valid for generation
  const isFormValid = selectedPipeIds.length > 0 &&
    selectedMaterialCategories.length > 0 &&
    signatureData?.signature?.qc &&
    signatureData?.signature?.customer;

  // Check if report can be generated: form valid + draft saved + no pending changes + report permission
  const canGenerate = isFormValid && hasSavedDraft && !isDirty && canSign;

  // Get tooltip message for generate button based on current state
  const getGenerateButtonTooltip = () => {
    if (!canSign) {
      return 'You do not have permission to generate this report';
    }
    if (!isFormValid) {
      if (selectedPipeIds.length === 0) {
        return 'Select at least one pipe size';
      }
      if (selectedMaterialCategories.length === 0) {
        return 'Select at least one material category';
      }
      if (!signatureData?.signature?.qc || !signatureData?.signature?.customer) {
        return 'Both QC and Customer signatures are required';
      }
      return 'Complete all required fields';
    }
    if (!hasSavedDraft) {
      return 'Save draft before generating report';
    }
    if (isDirty) {
      return 'Save your changes before generating report';
    }
    return '';
  };

  // Close PDF viewer
  const closeViewer = () => {
    setViewerFile(null);
  };

  // If viewer is open, render only the viewer
  if (viewerFile) {
    return (
      <PdfViewer
        fileUrl={viewerFile.fileUrl}
        fileName={viewerFile.fileName}
        onClose={closeViewer}
      />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="w-screen h-screen max-w-none m-0 p-0 gap-0 border-0 bg-white shadow-none rounded-none [&>button]:hidden"
      >
        <div className="flex flex-col relative z-20 h-screen overflow-hidden">
          <DialogHeader className="px-4 sm:px-6 py-4 border-b flex flex-row items-center justify-between bg-white relative z-10 flex-shrink-0">
            <div className="flex items-center gap-4">
              <Button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                variant="outline"
                disabled={isLoading}
              >
                <ArrowLeft className="h-5 w-5 text-gray-500" />
              </Button>
              <div>
                <DialogTitle className="text-lg font-bold text-gray-900">
                  {selectedTabName} Traceability Report
                  {isLoading && (
                    <span className="ml-2 text-sm font-normal text-red-600">
                      (Generating...)
                    </span>
                  )}
                </DialogTitle>
                {headerInfoItems.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {headerInfoItems.map((item, index) => (
                      <span key={index} className={`${item.bgColor} px-2 py-1 rounded text-xs font-medium`}>
                        {item.label}: {item.value}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogHeader>

          {/* Scrollable Content */}
          <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 relative">
            {/* Blur overlay with loader - only show during initial fetch, not during save draft or reload */}
            {isFetchingDraft && (
              <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-10 flex flex-col items-center justify-center rounded-lg">
                <Loader2 className="h-8 w-8 animate-spin text-[#EB1C37] mb-4" />
                <span className="text-sm text-gray-600">Loading draft data...</span>
              </div>
            )}
            {/* Subtle indicator during save draft - doesn't block interaction */}
            {isSavingDraft && (
              <div className="absolute top-4 right-4 bg-blue-50 border rounded-lg px-4 py-2 shadow-sm z-20 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                <span className="text-sm text-blue-700">Saving draft...</span>
              </div>
            )}
            {/* Form content - always visible, disabled only during initial draft fetch, not during save draft or reload */}
            <div className={cn("space-y-6", isFetchingDraft && "pointer-events-none opacity-50")}>

              <>
              </>

              {/* Project Information - Read Only */}
              {/* <div className="p-4 bg-blue-50 border rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 space-y-1 text-sm text-blue-700">
                    <p className="font-medium">Report Configuration:</p>
                    <p>• Project ID: <span className="font-semibold">{projectId}</span></p>
                    <p>• Report Type: <span className="font-semibold">Material Traceability Report</span></p>
                    <p>• Selected Pipes: <span className="font-semibold">{selectedPipeIds.length} of {pipeSizesDataProp.length}</span></p>
                  </div>
                </div>
              </div> */}

              {/* Pipe Selection Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Select Pipe Sizes *</Label>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedPipeIds.length} of {pipeSizesDataProp.length} pipes selected
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAllPipes}
                    disabled={pipeSizesDataProp.length === 0 || !canEdit}
                  >
                    {selectedPipeIds.length === pipeSizesDataProp.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>

                {pipeSizesDataProp.length === 0 ? (
                  <div className="flex items-center gap-2 p-4 bg-yellow-50 border rounded-lg">
                    <AlertCircle className="h-5 w-5 text-yellow-600" />
                    <span className="text-sm text-yellow-700">No pipe sizes found for this project.</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2 max-h-[200px] overflow-y-auto border rounded-lg p-3 bg-gray-50">
                    {pipeSizesDataProp.map((pipe) => {
                      const isSelected = selectedPipeIds.includes(pipe.pipeId.toString());
                      return (
                        <label
                          key={pipe.pipeId}
                          className={cn(
                            "flex items-center gap-3 p-4 border-2 rounded-lg transition-all",
                            canEdit ? "cursor-pointer" : "cursor-not-allowed opacity-70",
                            isSelected
                              ? "border-[#EB1C37] bg-red-50"
                              : "border-gray-200 hover:border-gray-300"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handlePipeToggle(pipe.pipeId.toString())}
                            disabled={!canEdit}
                            className="h-5 w-5 text-[#EB1C37] focus:ring-[#EB1C37] accent-[#EB1C37] rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <p className={cn(
                              "text-base font-medium truncate",
                              isSelected ? "text-gray-900" : "text-gray-600"
                            )}>
                              {pipe.pipeName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {pipe.size}" x {pipe.wallThickness}"
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Material Category Selection Section */}
              {/* <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Select Material Categories *</Label>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedMaterialCategories.length} of {getAvailableMaterialCategories().length} categories selected
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAllCategories}
                    disabled={getAvailableMaterialCategories().length === 0}
                  >
                    {selectedMaterialCategories.length === getAvailableMaterialCategories().length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>

                {getAvailableMaterialCategories().length === 0 ? (
                  <div className="flex items-center gap-2 p-4 bg-yellow-50 border rounded-lg">
                    <AlertCircle className="h-5 w-5 text-yellow-600" />
                    <span className="text-sm text-yellow-700">Please select at least one pipe size first to see available material categories.</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2 max-h-[200px] overflow-y-auto border rounded-lg p-3 bg-gray-50">
                    {getAvailableMaterialCategories().map((category) => {
                      const isSelected = selectedMaterialCategories.includes(category);
                      const categoryLabels: Record<string, string> = {
                        'pipe': 'Pipe',
                        'forge': 'Forge',
                        'gas': 'Gas',
                        'wire': 'Wire',
                        'coating': 'Coating'
                      };
                      return (
                        <label
                          key={category}
                          className={cn(
                            "flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all",
                            isSelected
                              ? "border-[#EB1C37] bg-red-50"
                              : "border-gray-200 hover:border-gray-300"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleMaterialCategoryToggle(category)}
                            className="h-5 w-5 text-[#EB1C37] focus:ring-[#EB1C37] accent-[#EB1C37] rounded"
                          />
                          <span className={cn(
                            "text-base font-medium",
                            isSelected ? "text-gray-900" : "text-gray-600"
                          )}>
                            {categoryLabels[category] || category}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div> */}

              {/* Signature Section */}
              <Signature
                customerSignature={selectedCustomerRepresentativeId}
                qcSignature={selectedQcInspectorId}
                customerRepresentativeList={customerRepresentativeList}
                qcList={qcList}
                handleQcSignatureChange={handleQcSignatureChange}
                handleCustomerSignatureChange={handleCustomerSignatureChange}
                signatureData={signatureData}
                onSignatureChange={onHandleSignature}
                canSign={canSign}
                canEdit={canEdit}
                isEditMode={canEdit}
                isCustomerRepresentativeUser={isCustomerRepresentativeUser}
                isQCInspectorUser={isQCInspectorUser}
                isWelderUser={isWelderUser}
                currentUserTeamMemberId={currentUserTeamMemberId ?? undefined}
                isAdminOrSuperAdmin={isAdminOrSuperAdmin}
              />

              {/* Email Input Section - Commented out */}
              {/* <div className="space-y-3 pt-6 border-t">
                <Label className="text-sm font-medium text-gray-700">
                  Email Addresses (Optional)
                  <span className="ml-2 text-xs text-gray-500 font-normal">
                    Add multiple emails to receive the report
                  </span>
                </Label>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Input
                        type="email"
                        placeholder="Enter email address and press Enter or click Add"
                        value={email}
                        onChange={handleEmailChange}
                        onKeyPress={handleKeyPress}
                        className={cn(
                          emailError ? '' : ''
                        )}
                        disabled={isLoading || isSuccess}
                      />
                      {emailError && (
                        <p className="text-xs text-red-600 mt-1">{emailError}</p>
                      )}
                    </div>
                    <Button
                      onClick={handleAddEmail}
                      size="sm"
                      variant="outline"
                      className="h-10 px-4"
                      disabled={!email.trim() || isLoading || isSuccess}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>

                  {emailList.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      {emailList.map((emailAddr, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-1 px-3 py-1.5 text-blueprint-blue bg-[rgba(0,39,63,0.06)] rounded-md text-sm"
                        >
                          <span>{emailAddr}</span>
                          {!isLoading && !isSuccess && (
                            <button
                              onClick={() => handleRemoveEmail(emailAddr)}
                              className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {emailList.length === 0 && (
                    <p className="text-xs text-gray-500">
                      No email addresses added. Report will be generated without email notification.
                    </p>
                  )}
                </div>
              </div> */}

              {/* Report Information */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">Report Information</Label>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="space-y-3 text-sm text-blue-700">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      <span>Comprehensive material traceability report for selected pipe size</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <span>Includes all material traceability data for the selected pipe size</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Success Message */}
              {isSuccess && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div className="text-sm text-green-700">
                    {emailList.length > 0 ? (
                      <span>Report generation initiated successfully! Check your email{emailList.length > 1 ? 's' : ''} for the report.</span>
                    ) : (
                      <span>Report generation initiated successfully!</span>
                    )}
                  </div>
                </div>
              )}

              {/* Error Message */}
              {generateMaterialReportError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <div className="text-sm text-red-700">
                    <div className="font-medium">Error: {generateMaterialReportError}</div>
                  </div>
                </div>
              )}

              {/* Draft Status Info */}
              {(saveDraftSuccess || saveDraftError || missingFields.length > 0) && (
                <div className="space-y-2">
                  {/* {saveDraftSuccess && (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 border rounded-lg">
                      <CheckCircle className="h-5 w-5 text-blue-600" />
                      <div className="text-sm text-blue-700">
                        <span>Draft saved successfully!</span>
                      </div>
                    </div>
                  )} */}
                  {saveDraftError && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border rounded-lg">
                      <AlertCircle className="h-5 w-5 text-red-600" />
                      <div className="text-sm text-red-700">
                        <span>Failed to save draft: {saveDraftError}</span>
                      </div>
                    </div>
                  )}
                  {missingFields.length > 0 && !isReadyToGenerate && (
                    <div className="flex items-start gap-2 p-3 bg-yellow-50 border rounded-lg">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div className="text-sm text-yellow-700">
                        <span className="font-medium">Missing required fields:</span>
                        <ul className="list-disc list-inside mt-1">
                          {missingFields.map((field: string, index: number) => (
                            <li key={index}>{field}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-6 border-t">
                {/* <Button
                  onClick={onClose}
                  variant="outline"
                  className="px-6"
                  disabled={isLoading || isSavingDraft}
                >
                  {isSuccess ? 'Close' : 'Cancel'}
                </Button> */}

                {(
                  <>
                    <Button
                      onClick={handleSaveDraft}
                      variant="outline"
                      className="px-6"
                      disabled={isSavingDraft || isLoading || !isDirty || !canEdit}
                      title={!canEdit ? 'You do not have permission to edit this report' : !isDirty ? 'No changes to save' : ''}
                    >
                      {isSavingDraft ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </>
                      )}
                    </Button>
                    <GenerateReportButton
                      moduleName="Report"
                      onClick={handleGenerateReport}
                      isLoading={isLoading}
                      disabled={!canGenerate || isSavingDraft || !projectId}
                      title={getGenerateButtonTooltip()}
                      className="px-6"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

